import { useEffect } from 'react'
import type { JSX } from 'react'
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom'

import * as Sentry from '@sentry/react'

import {
  usePageLoad,
  useCurrentOrganization,
  useIsOneBufferOrg,
} from '~publish/contexts/AccountContext'
import { InvitationModalContainer } from '~publish/components/InvitationModal'
import { getProductAreaTitle } from '~publish/helpers/getProductAreaTitle'
import { useConnectChannelModal } from '~publish/hooks/useConnectChannelModal'
import { usePageTitle } from '~publish/hooks/usePageTitle'
import { usePostComposer } from '~publish/hooks/usePostComposer'
import { useStreaksAutoRefresh } from '~publish/hooks/useStreaksAutoRefresh'
import { CALENDAR_PAGE } from '~publish/legacy/constants'
import { setLastVisitedPage } from '~publish/legacy/utils/page-visits'
import { useCanAccessTeamFeatures } from '~publish/pages/Settings/Team/hooks/useCanAccessTeamFeatures'

import { renderAppRoutes } from './AppRoutes'

declare global {
  interface Window {
    __isComposerOpen?: boolean
  }
}

const CatchAllRedirect = (): JSX.Element => {
  const location = useLocation()

  if (location.pathname !== '/') {
    // eslint-disable-next-line no-console
    console.log('Catch-all route triggered for path: ' + location.pathname)
    Sentry.withScope((scope) => {
      scope.setTag('source', 'catch-all-route')
      scope.setExtras({
        pathname: location.pathname,
        searchKeys: location.search
          ? [...new URLSearchParams(location.search).keys()]
          : [],
        hasHash: Boolean(location.hash),
      })
      Sentry.captureException(
        new Error('Catch-all route triggered for path: ' + location.pathname),
      )
    })
  }

  return <Navigate to="/all-channels" replace />
}

export const AppPages = (): JSX.Element => {
  const location = useLocation()

  usePageTitle(getProductAreaTitle(location.pathname))

  // Get current selected org from account
  const { createNewPostInComposer, isOpen: isComposerOpen } = usePostComposer()
  const navigate = useNavigate()
  useEffect(() => {
    // Make post composer and idea composer available globally so that it can be used in the app shell
    window.__openComposer = createNewPostInComposer
    window.__isComposerOpen = isComposerOpen
  }, [createNewPostInComposer, isComposerOpen])

  const { updateInitialPageLoad } = usePageLoad()

  useEffect(() => {
    updateInitialPageLoad()
  })

  /**
   * IMPORTANT NOTE: avoid to place here any hook that contains state vars
   * as it will re-render the entire router.
   */
  useConnectChannelModal()
  useStreaksAutoRefresh()

  const [searchParams] = useSearchParams()
  // eslint-disable-next-line
  const channelSelectedThroughRoute = (location.state as { channel?: string })
    ?.channel

  const shouldPreOpenChannelStorefront =
    searchParams.get('channelStorefront') === 'true'

  useEffect(() => {
    // Reset channel to select state included in some location changes
    if (channelSelectedThroughRoute) {
      navigate(location.pathname, { replace: true, state: {} })
    }

    /* TODO: we could include here others setLastVisitedPage calls, as the one for queue, for example */
    if (location.pathname.includes('calendar')) {
      setLastVisitedPage(CALENDAR_PAGE)
    }
  }, [location, channelSelectedThroughRoute, navigate])

  const currentOrganization = useCurrentOrganization()
  const isOneBufferOrganization = useIsOneBufferOrg()
  const showBetaFeatures = Boolean(
    !currentOrganization || isOneBufferOrganization,
  )
  const { canAccessTeamFeatures, isLoading: isLoadingTeamFeatures } =
    useCanAccessTeamFeatures()

  const loadTeamRoute =
    isLoadingTeamFeatures || (canAccessTeamFeatures && !isLoadingTeamFeatures)

  return (
    <>
      <Routes>
        {/* ================================================================ */}
        {/*  ROUTES                                                          */}
        {/*  @see ./AppRoutes.tsx                                            */}
        {/* ================================================================ */}
        {renderAppRoutes({
          shouldPreOpenChannelStorefront,
          showBetaFeatures,
          loadTeamRoute,
        })}

        {/* ================================================================ */}
        {/*  CATCH-ALL                                                       */}
        {/* ================================================================ */}
        <Route path="*" element={<CatchAllRedirect />} />
      </Routes>

      {/* Surfaces the invitation accept/welcome modal on top of whatever page
          the user lands on, when they arrive via an invite link (CORE-6022). */}
      <InvitationModalContainer />
    </>
  )
}
